import React from 'react';
import { useNotify } from 'react-admin';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { hostname } from '../providers/api';
import { useTokenOps } from '../components/TokenContext';

/** Admin token: root first (has the ADMIN role), switched account as fallback. */
export const useAdminToken = () => {
  const tokenOps = useTokenOps();
  return tokenOps.root?.accessToken || tokenOps.account?.accessToken;
};

export const adminFetch = async (path, token, options = {}) => {
  const response = await fetch(`${hostname}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (body?.message) {
        message = Array.isArray(body.message)
          ? body.message.join(', ')
          : body.message;
      }
    } catch (error) {
      // Body is not JSON: keep the status message.
    }
    throw new Error(message);
  }
  return response.json();
};

// Same label override as the legacy account features screen.
const FEATURE_LABELS = { Integration: 'API Integration (Side Lateral)' };
export const featureLabel = (name) => FEATURE_LABELS[name] || name;

/** `app_account.inactive` drives access (login, API, DPP). */
export const accessLabel = (inactive) => (inactive ? 'Disabled' : 'Enabled');

const sameKeys = (a, b) =>
  a.size === b.size && [...a].every((key) => b.has(key));

/**
 * Shows and edits one account's active state and enabled features through
 * the admin-only `GET/PUT /adminAccountFeatures/:accountId` endpoints.
 */
const AccountFeaturesEditor = ({ accountId }) => {
  const token = useAdminToken();
  const notify = useNotify();

  const [detail, setDetail] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [inactive, setInactive] = React.useState(false);
  const [status, setStatus] = React.useState('');
  const [selected, setSelected] = React.useState(new Set());
  const [saving, setSaving] = React.useState(false);

  const applyDetail = React.useCallback((data) => {
    setDetail(data);
    setInactive(Boolean(data.inactive));
    setStatus(data.status || '');
    setSelected(new Set(data.features.map((feature) => feature.key)));
  }, []);

  React.useEffect(() => {
    let active = true;
    setDetail(null);
    setError(null);
    if (!accountId) return undefined;
    adminFetch(`/adminAccountFeatures/${accountId}`, token)
      .then((data) => active && applyDetail(data))
      .catch((err) => active && setError(err.message));
    return () => {
      active = false;
    };
  }, [accountId, token, applyDetail]);

  if (!accountId) {
    return <Typography>No account selected.</Typography>;
  }
  if (error) {
    return (
      <Typography color="error" role="alert">
        Unable to load the account: {error}
      </Typography>
    );
  }
  if (!detail) return <CircularProgress size={24} />;

  const savedKeys = new Set(detail.features.map((feature) => feature.key));
  const accessChanged = inactive !== Boolean(detail.inactive);
  const statusChanged = status !== (detail.status || '');
  const featuresChanged = !sameKeys(selected, savedKeys);
  const dirty = accessChanged || statusChanged || featuresChanged;

  const toggleFeature = (key) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (
      accessChanged &&
      inactive &&
      !window.confirm(
        `Deactivate ${detail.name}? Its users will not be able to log in and its API and DPP stop serving.`,
      )
    ) {
      return;
    }
    const body = {};
    if (accessChanged) body.inactive = inactive;
    if (statusChanged) body.status = status;
    if (featuresChanged) body.featureKeys = [...selected];
    setSaving(true);
    try {
      const data = await adminFetch(`/adminAccountFeatures/${accountId}`, token, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      applyDetail(data);
      notify('Account updated');
    } catch (err) {
      notify(`Error: ${err.message}`, 'warning');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6">{detail.name}</Typography>
      <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 24 }}>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={!inactive}
                onChange={(event) => setInactive(!event.target.checked)}
                color="primary"
                inputProps={{ 'aria-label': 'Access enabled' }}
              />
            }
            label={`Access: ${accessLabel(inactive)}`}
          />
          <Typography variant="caption" color="textSecondary" component="div">
            Disabled accounts cannot log in and their API and DPP stop serving.
          </Typography>
        </Box>
        <Box>
          <TextField
            select
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Status' }}
            style={{ minWidth: 180 }}
          >
            {!detail.status && <option value="">—</option>}
            {(detail.availableStatuses || []).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </TextField>
          <Typography variant="caption" color="textSecondary" component="div">
            Commercial classification only: it does not change access.
          </Typography>
        </Box>
      </Box>

      <Typography variant="subtitle1" style={{ marginTop: 16 }}>
        Features
      </Typography>
      <FormGroup>
        {detail.availableFeatures.map((feature) => (
          <FormControlLabel
            key={feature.key}
            control={
              <Checkbox
                checked={selected.has(feature.key)}
                onChange={() => toggleFeature(feature.key)}
                color="primary"
              />
            }
            label={
              <span>
                {featureLabel(feature.name)}{' '}
                <Typography
                  component="span"
                  variant="caption"
                  color="textSecondary"
                >
                  {feature.key}
                </Typography>
              </span>
            }
          />
        ))}
      </FormGroup>

      <Box marginTop={2} display="flex" style={{ gap: 8 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button
          onClick={() => applyDetail(detail)}
          disabled={!dirty || saving}
        >
          Discard changes
        </Button>
      </Box>
    </Box>
  );
};

export default AccountFeaturesEditor;
